import { action, observable } from 'mobx';
import { IRootStore } from '@stores/index';
import { apiAuthCaller } from '@services/apiCaller';
import { getApiErrorMessage } from '@components/common/util/util';
import { analytics } from '@lib/analytics';
import { BI_EVENTS, TRACK_TYPE } from '@lib/analytics/events';

export interface ITeamStore {
  rootStore: IRootStore;
  inviteUser: (email: string, role: string) => void;
  deleteUserInvitation: (id: string) => void;
  updateUserInvitation: (email: string, id: string, role: string) => void;
  removeUser: (id: string) => void;
  updateUserRole: (id: string, role: string) => void;
}

export class TeamStore implements ITeamStore {
  @observable public rootStore: IRootStore;

  constructor(rootStore: IRootStore) {
    this.rootStore = rootStore;
  }

  @action.bound
  public async inviteUser(email: string, role: string) {
    const { id: organizationId } = this.rootStore.organizationStore;
    const { token } = this.rootStore.userStore;
    const { messagesStore } = this.rootStore;
    try {
      await apiAuthCaller(token).post('/inviteUser', {
        email,
        name: '',
        role,
        organizationId,
      });
      messagesStore!.showSuccessMessage(`Invitation sent to ${email}`);
      analytics.track2(BI_EVENTS.INVITE_TEAM_MEMBERS, TRACK_TYPE.API_SUCCESS, {
        teammate_email: email,
        teammate_permission: role,
      });
    } catch (err) {
      messagesStore!.showErrorMessage(getApiErrorMessage(err, 'Failed to invite'));
    }
  }

  @action.bound
  public async deleteUserInvitation(id: string) {
    const { token } = this.rootStore.userStore;
    await apiAuthCaller(token).delete(`/userInvitations/${id}`);
  }

  @action.bound
  public async updateUserInvitation(email: string, id: string, role: string) {
    const { token } = this.rootStore.userStore;
    const { id: organizationId } = this.rootStore.organizationStore;
    await apiAuthCaller(token).post(`/updateInvitation/${id}`, { organizationId, email, role });
  }

  @action.bound
  public async removeUser(id: string) {
    const { token } = this.rootStore.userStore;
    const { id: organizationId } = this.rootStore.organizationStore;
    await apiAuthCaller(token).delete(`/users/${id}`, { data: { organizationId } });
  }

  @action.bound
  public async updateUserRole(id: string, role: string) {
    const { token } = this.rootStore.userStore;
    const { id: organizationId } = this.rootStore.organizationStore;
    await apiAuthCaller(token).post(`/updateUser/${id}`, { role, organizationId });
  }
}
